/**
 * What a segment's source text is: the string a translator sees and the string
 * `sourceHash` runs over.
 *
 * Every walk canonicalizes through this one module. Two walks producing different
 * bytes for the same visible line would break the hash guard, so there is exactly one
 * definition rather than one per walk.
 */

import { decodeHTML } from "entities";

import { hasDynamicSlotMarkers } from "../types/dynamic-slot-markers";

/** `&amp;` is decoded last so `&amp;lt;` survives as the literal `&lt;`. */
export function decodeMarkupEntities(value: string): string {
  return value
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#0*39;|&#x0*27;|&apos;/gi, "'")
    .replace(/&amp;/g, "&");
}

/**
 * The Beefree canvas stores merge tags as decorated `<code>` pills, carrying the Liquid
 * either in `data-bee-value` or in the element's own text. Exported HTML carries plain
 * Liquid, so plain Liquid is the canonical form both must hash to.
 */
export function normalizeMergeTagPills(html: string): string {
  return html.replace(
    /<code\b([^>]*\bdata-bee-type="mergetag"[^>]*)>([\s\S]*?)<\/code>/gi,
    (_match, attributes: string, inner: string) => {
      const value = /\bdata-bee-value="([^"]*)"/i.exec(attributes);
      return decodeMarkupEntities(value?.[1] ?? inner);
    },
  );
}

/** `a` is included: a whole-line link peels to clean text, its href extracted apart. */
export const PEELABLE_INLINE_TAGS = "span|b|strong|i|em|u|s|small|mark|font|a";

/**
 * Tags that delimit one visible line. A leaf one of these is exactly one segment.
 *
 * `td`/`th` are here for hand-written HTML, where text often sits straight in a cell
 * with no `<p>` around it. A cell wrapping block content is not a leaf, so table
 * layouts still segment on the inner blocks.
 */
export const SEGMENT_BLOCK_TAGS = new Set([
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "li",
  "blockquote",
  "div",
  "td",
  "th",
]);

/**
 * Must not be written as `<!--[\s\S]*?-->`: a lazy body may stop at any later `-->`, so N
 * adjacent comments have 2^(N-1) parses, and `EDGE_NOISE` puts this alternative under a
 * `(?:…)+`. The alternatives below are disjoint, so each comment has exactly one parse.
 */
const HTML_COMMENT = String.raw`<!--(?:[^-]|-(?!->))*-->`;

const ZERO_WIDTH_CHAR = "\u200b";

const ZERO_WIDTH = String.raw`&#0*8203;|&#x0*200b;|&zwj;|&zwnj;|​`;

const ZERO_WIDTH_AT_END_RE = new RegExp(`(?:${ZERO_WIDTH})$`, "i");

/**
 * Whitespace, the zero-width padding ESPs stuff into a preheader div, and comments.
 * Trimming these keeps an anchor range from starting or ending inside an Outlook
 * conditional, whose bytes a swap must never overwrite. `trailingNoiseLength` scans the
 * same three forms for the trailing edge, so a fourth has to be added in both places.
 */
const EDGE_NOISE = String.raw`\s|${ZERO_WIDTH}|${HTML_COMMENT}`;
export const LEADING_NOISE_RE = new RegExp(`^(?:${EDGE_NOISE})+`, "i");

function trailingZeroWidthLength(value: string, end: number): number {
  if (end === 0) return 0;
  if (value[end - 1] === ZERO_WIDTH_CHAR) return 1;
  if (value[end - 1] !== ";") return 0;
  const start = value.lastIndexOf("&", end - 1);
  if (start === -1) return 0;
  return ZERO_WIDTH_AT_END_RE.test(value.slice(start, end)) ? end - start : 0;
}

function trailingCommentLength(value: string, end: number): number {
  if (end < 7 || !value.startsWith("-->", end - 3)) return 0;
  const start = value.lastIndexOf("<!--", end - 4);
  if (start === -1) return 0;
  // A comment ends at its FIRST `-->`, so an opener that closes earlier means this
  // `-->` is literal text rather than a terminator.
  return value.indexOf("-->", start + 4) === end - 3 ? end - start : 0;
}

/**
 * Scanned backwards rather than matched with `(?:…)+$`, which is quadratic however
 * disjoint the alternatives are — the engine retries the repetition from every start
 * position inside the run. 4.5s on 64k trailing spaces, per leaf block of every render.
 */
export function trailingNoiseLength(value: string): number {
  let end = value.length;
  for (;;) {
    const before = end;
    while (end > 0 && /\s/.test(value[end - 1] as string)) end--;
    end -= trailingZeroWidthLength(value, end);
    end -= trailingCommentLength(value, end);
    if (end === before) return value.length - end;
  }
}

export function trimEdgeNoise(value: string): string {
  const trimmed = value.replace(LEADING_NOISE_RE, "");
  return trimmed.slice(0, trimmed.length - trailingNoiseLength(trimmed));
}

/**
 * Depth-walked rather than counted. In `<a>one</a> · <a>two</a>` the inner text has one
 * open and one close, so a count-based check concludes the leading `<a>` wraps
 * everything and peels it — mangling the text and pointing a swap into the middle of
 * markup.
 */
export function wrapsWholeSlice(
  slice: string,
  tag: string,
  openLength: number,
  closeLength: number,
): boolean {
  const inner = slice.slice(openLength, slice.length - closeLength);
  let depth = 0;
  for (const match of inner.matchAll(new RegExp(`<(/?)${tag}\\b`, "gi"))) {
    depth += match[1] === "/" ? -1 : 1;
    if (depth < 0) return false;
  }
  return depth === 0;
}

/**
 * Peeling is not deletion: the wrapper stays in the document and the swap anchor moves
 * inside it. Beefree's HTML export keeps its editor wrapper, so the offset-aware twin
 * has to descend the same path.
 *
 * Only an unambiguous whole-value wrapper is peeled, so `<b>a</b><b>c</b>` — which also
 * starts with `<b>` and ends with `</b>` — is left alone.
 */
export function peelWholeSegmentWrappers(html: string): {
  text: string;
  /** Outermost first. A peeled `<a>` still owns an href. */
  peeled: string[];
} {
  const openRe = new RegExp(`^<(${PEELABLE_INLINE_TAGS})\\b[^>]*>`, "i");
  const peeled: string[] = [];
  let current = trimEdgeNoise(html);
  for (;;) {
    const open = openRe.exec(current);
    if (!open || open[1] === undefined) return { text: current, peeled };
    const tag = open[1].toLowerCase();
    const close = new RegExp(`</${tag}\\s*>$`, "i").exec(current);
    if (!close) return { text: current, peeled };
    if (!wrapsWholeSlice(current, tag, open[0].length, close[0].length))
      return { text: current, peeled };
    peeled.push(open[0]);
    current = trimEdgeNoise(current.slice(open[0].length, -close[0].length));
  }
}

/**
 * A pure-Liquid href like `{{ ["unsubscribe_url"] }}` or a Dynamic content marker is
 * generated per recipient, so a translated copy of it would be actively wrong.
 */
export function isLocalizableUrl(url: string): boolean {
  const trimmed = url.trim();
  if (trimmed.length === 0 || trimmed === "#") return false;
  if (hasDynamicSlotMarkers(trimmed)) return false;
  // An unclosed `{{` means the value was truncated out of malformed markup.
  if (trimmed.includes("{{") && !trimmed.includes("}}")) return false;
  return trimmed.replace(/\{\{[\s\S]*?\}\}/g, "").trim().length > 0;
}

/** Spreadsheets and Windows editors write CRLF, and the hash must not care. */
export function normalizeLineBreaks(value: string): string {
  return value.replace(/\r\n?/g, "\n");
}

/**
 * Load-bearing for the hash, not cosmetic. Saving a message re-indents the body, so a
 * line's bytes change while its copy does not; hashing raw bytes would strand every
 * translation of an untouched line on a save that changed nothing.
 *
 * `<pre>` is exempt, since there whitespace is content.
 */
export function collapseWhitespace(value: string): string {
  if (/<pre\b/i.test(value)) return value;
  return value.replace(/\s+/g, " ").trim();
}

/**
 * Markup-free values only: inside a tag an entity can be load-bearing, and
 * `title="a &quot;b&quot;"` breaks if decoded. `&lt;`/`&gt;`/`&amp;` are never decoded,
 * which would manufacture markup out of escaped text.
 */
function decodePlainTextQuotes(value: string): string {
  if (value.includes("<")) return value;
  return value
    .replace(/&#0*39;|&#x0*27;|&apos;/gi, "'")
    .replace(/&quot;/g, '"');
}

/** A raw source value to its canonical source text. */
export function normalizeSourceHtml(html: string): {
  text: string;
  peeled: string[];
} {
  const { text, peeled } = peelWholeSegmentWrappers(html);
  return {
    text: collapseWhitespace(
      decodePlainTextQuotes(normalizeMergeTagPills(text)),
    ),
    peeled,
  };
}

/** A content block owns its own translations, so its embed is not a message's row. */
export function isLiquidTagOnly(text: string): boolean {
  return text.replace(/\{%[\s\S]*?%\}/g, "").trim().length === 0;
}

/** Any script's letters and digits. A value carrying none of them (a spacer cell, a
 *  divider's bullet, zero-width padding) reads the same in every language. */
const TRANSLATABLE_CHAR_RE = /[\p{L}\p{N}]/u;

/**
 * Entities are decoded first because the value is raw markup: a Beefree spacer writes
 * its hair space as `&#8202;`, whose own digits would otherwise read as copy.
 */
export function isUntranslatableText(text: string): boolean {
  return !TRANSLATABLE_CHAR_RE.test(decodeHTML(text));
}
